import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { pusherService } from "@/lib/pusher/pusher-service";
import { PusherCartUpdatePayload } from "@/lib/pusher/types";
import { actionCableClient } from "@/lib/actioncable/actioncable-client";
import { actionCableService } from "@/lib/actioncable/actioncable-service";
import { ActionCableCartUpdatePayload } from "@/lib/actioncable/types";
import { CartResponse } from "@/networking/carts.api";
import { useRealtime } from "@/components/providers/RealtimeProvider";

type CartUpdatePayload = PusherCartUpdatePayload | ActionCableCartUpdatePayload;

export function useRealtimeCartSync(
  cartToken: string,
  enabled = true,
  paymentMethodsIncludes?: string | null,
) {
  const queryClient = useQueryClient();
  const {
    isPusherEnabled,
    connectionStatus,
    isActionCableEnabled,
    actionCableUrl,
    setActionCableConnectionStatus,
  } = useRealtime();

  const pusherCleanupRef = useRef<(() => void) | null>(null);
  const actionCableCleanupRef = useRef<(() => void) | null>(null);
  const [isPusherSubscribed, setIsPusherSubscribed] = useState(false);
  const [isActionCableSubscribed, setIsActionCableSubscribed] = useState(false);

  const handleUpdate = useEffectEvent(
    (payload: CartUpdatePayload, source: "pusher" | "actioncable") => {
      try {
        const cartQueryKey = ["cart", cartToken, paymentMethodsIncludes];

        queryClient.setQueryData<CartResponse>(cartQueryKey, (oldData) => {
          if (!oldData) {
            console.warn(
              "[RealtimeCartSync] No existing cart data in cache, creating new",
            );
            // When payment_methods_includes is active, skip seeding the cache
            // from a realtime event that may arrive before the initial fetchCart
            // has persisted the filter to cart metadata. Let fetchCart populate
            // the cache with the correctly filtered data first.
            if (paymentMethodsIncludes) {
              return oldData;
            }
            return {
              cart: payload.cart,
              meta: payload.meta,
            };
          }

          return {
            cart: {
              ...oldData.cart,
              ...payload.cart,
              // Realtime broadcasts don't respect payment_methods_includes,
              // so preserve the cached (filtered) list when the filter is active.
              ...(paymentMethodsIncludes && {
                available_payment_methods:
                  oldData.cart.available_payment_methods,
              }),
            },
            meta: {
              ...oldData.meta,
              ...payload.meta,
            },
          };
        });
      } catch (error) {
        console.error(
          `[RealtimeCartSync] Error handling update from ${source}:`,
          error,
        );
      }
    },
  );

  // Pusher subscription — isolated so reconnects don't tear down ActionCable
  useEffect(() => {
    console.log("[RealtimeCartSync] Pusher effect running:", {
      enabled,
      cartToken: !!cartToken,
      isPusherEnabled,
      isConnected: connectionStatus.isConnected,
      connectionState: connectionStatus.state,
    });

    if (
      !enabled ||
      !cartToken ||
      !isPusherEnabled ||
      !connectionStatus.isConnected
    ) {
      return;
    }

    console.log(
      `[RealtimeCartSync] Pusher connected, subscribing to cart updates for ${cartToken}`,
    );
    const pusherCleanup = pusherService.subscribeToCartUpdates(
      cartToken,
      (payload) => handleUpdate(payload, "pusher"),
    );
    pusherCleanupRef.current = pusherCleanup;
    Promise.resolve().then(() => setIsPusherSubscribed(true));

    return () => {
      if (pusherCleanupRef.current) {
        pusherCleanupRef.current();
        pusherCleanupRef.current = null;
        setIsPusherSubscribed(false);
      }
    };
  }, [cartToken, enabled, isPusherEnabled, connectionStatus.isConnected]);

  // ActionCable subscription — independent of Pusher connection state
  useEffect(() => {
    if (!enabled || !cartToken || !isActionCableEnabled || !actionCableUrl) {
      return;
    }

    try {
      actionCableClient.initialize(actionCableUrl, cartToken);

      setActionCableConnectionStatus({
        state: "connected",
        isConnected: true,
      });

      const actionCableCleanup = actionCableService.subscribeToCartUpdates(
        cartToken,
        (payload) => handleUpdate(payload, "actioncable"),
      );
      actionCableCleanupRef.current = actionCableCleanup;
      Promise.resolve().then(() => setIsActionCableSubscribed(true));
    } catch (error) {
      console.error(
        "[RealtimeCartSync] ActionCable initialization failed:",
        error,
      );
      setActionCableConnectionStatus({
        state: "disconnected",
        isConnected: false,
        error: error as Error,
      });
    }

    return () => {
      if (actionCableCleanupRef.current) {
        actionCableCleanupRef.current();
        actionCableCleanupRef.current = null;
        setIsActionCableSubscribed(false);
      }

      actionCableClient.disconnect();
    };
  }, [
    cartToken,
    enabled,
    isActionCableEnabled,
    actionCableUrl,
    setActionCableConnectionStatus,
  ]);

  return {
    isPusherSubscribed,
    isActionCableSubscribed,
  };
}
